import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, qs } from '../lib/api';
import type { Page } from '../lib/api';
import { Card, Empty, Pagination, TableSkeleton } from './ui';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Right-aligns and tabular-numbers the cell. */
  numeric?: boolean;
  width?: number | string;
}

/**
 * Filters and page live in the URL, not component state.
 *
 * That is deliberate: an operator working a queue needs to send a colleague
 * "the rejected KYC list" as a link, and needs the back button to return to
 * the page they were on rather than to page one.
 */
export function useListParams(defaults: Record<string, string> = {}) {
  const [params, setParams] = useSearchParams();

  const get = (key: string) => params.get(key) ?? defaults[key] ?? '';
  const page = Number(params.get('page') ?? 1);

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // Any filter change invalidates the current page number.
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setParams(next);
  };

  return { get, set, page, setPage, all: Object.fromEntries(params) };
}

export function DataTable<T extends { id: string }>({
  queryKey,
  path,
  params,
  columns,
  onRowClick,
  toolbar,
  emptyTitle = 'Nothing here yet',
  emptyBody,
  emptyIcon,
  footer,
  limit = 25,
  refetchInterval,
}: {
  queryKey: unknown[];
  path: string;
  params?: Record<string, unknown>;
  columns: Array<Column<T>>;
  onRowClick?: (row: T) => void;
  toolbar?: ReactNode;
  emptyTitle?: string;
  emptyBody?: string;
  emptyIcon?: string;
  footer?: (page: Page<T>) => ReactNode;
  limit?: number;

  /**
   * Milliseconds between background refreshes.
   *
   * For lists that fill from somewhere else while an operator is looking at
   * them — a member joining in the app — so the page does not have to be
   * reloaded to show them.
   */
  refetchInterval?: number;
}) {
  const { page, setPage } = useListParams();

  const query = useQuery({
    queryKey: [...queryKey, params, page],
    queryFn: () => api.get<Page<T>>(`${path}${qs({ ...params, page, limit })}`),
    placeholderData: (previous) => previous,
    refetchInterval,
  });

  const rows = query.data?.items ?? [];

  return (
    <>
      {toolbar && <div className="filters">{toolbar}</div>}
      <Card flush>
        {query.isError ? (
          <Empty
            icon="⚠️"
            title="Could not load this list"
            body={(query.error as Error).message}
          />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    {columns.map((c) => (
                      <th key={c.key} style={{ width: c.width, textAlign: c.numeric ? 'right' : undefined }}>
                        {c.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                {query.isLoading ? (
                  <TableSkeleton cols={columns.length} />
                ) : (
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        className={onRowClick ? 'clickable' : undefined}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                      >
                        {columns.map((c) => (
                          <td key={c.key} className={c.numeric ? 'cell-num' : undefined}>
                            {c.render(row)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>

            {!query.isLoading && rows.length === 0 && (
              <Empty icon={emptyIcon} title={emptyTitle} body={emptyBody} />
            )}

            {query.data && footer?.(query.data)}

            {query.data && (
              <Pagination
                page={query.data.meta.page}
                totalPages={query.data.meta.totalPages}
                total={query.data.meta.total}
                limit={query.data.meta.limit}
                onPage={setPage}
              />
            )}
          </>
        )}
      </Card>
    </>
  );
}
